import { useState, useCallback, useRef, useEffect } from "preact/hooks";
import type { ChatMessage, SessionUsage } from "../types";
import type { WidgetClient } from "../api/client";

interface UseChatOptions {
  client: WidgetClient;
  conversationId: string;
  /**
   * Creates the conversation on first send and returns its id. The widget no longer
   * opens one when the panel opens: 43% of opens in TME 310 never sent a message, and
   * each still made an empty conversation that counted against the guest cap.
   */
  ensureConversation?: () => Promise<string>;
  initialMessages?: ChatMessage[];
  /** Called when the SSE `done` event carries a server-authoritative usage snapshot. */
  onUsage?: (usage: SessionUsage) => void;
}

interface UseChatReturn {
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  errorCode: string | null;
  retryable: boolean;
  sendMessage: (content: string) => Promise<void>;
  retry: () => Promise<void>;
  clearError: () => void;
  resetMessages: (msgs?: ChatMessage[]) => void;
}

/**
 * SSE streaming chat hook — ported from frontend/src/hooks/useChat.ts.
 * Uses Preact hooks and WidgetClient instead of direct fetch.
 *
 * SSE protocol:
 *   event: token  → data: {"text": "chunk"}     → append to assistant message
 *   event: done   → data: {"messageId": "uuid"}  → finalize message
 *   event: error  → data: {"code": "...", "message": "..."} → show error
 */
export function useChat({ client, conversationId, ensureConversation, initialMessages, onUsage }: UseChatOptions): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages || []);

  // Sync when initialMessages changes (restore saved session or clear on new chat)
  useEffect(() => {
    setMessages(initialMessages || []);
  }, [initialMessages]);

  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const lastMessageRef = useRef<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
    setErrorCode(null);
    setRetryable(false);
  }, []);
  const resetMessages = useCallback((msgs?: ChatMessage[]) => {
    setMessages(msgs || []);
    setError(null);
  }, []);

  const sendMessage = useCallback(
    async (content: string) => {
      if (isStreaming) return;

      setError(null);
      setRetryable(false);
      setIsStreaming(true);
      lastMessageRef.current = content;

      // Optimistically add user message
      const userMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        role: "user",
        content,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);

      // Create placeholder assistant message
      const assistantMessageId = `temp-assistant-${Date.now()}`;
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: "assistant",
        content: "",
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        // Inside the try: a refused create (conversation cap, sign-in required) is
        // reported like any other send failure, and the placeholder is removed.
        const convId = conversationId || (ensureConversation ? await ensureConversation() : "");
        const res = await client.sendMessage(convId, content, controller.signal);

        if (!res.ok) {
          const errBody = await res.json().catch(() => null);
          const code = errBody?.error?.code || `HTTP_${res.status}`;
          setErrorCode(code);
          throw new Error(errBody?.error?.message || `HTTP ${res.status}`);
        }

        const reader = res.body?.getReader();
        if (!reader) throw new Error("No response body");

        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          let currentEvent = "";
          for (const line of lines) {
            if (line.startsWith("event: ")) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith("data: ")) {
              const dataStr = line.slice(6);
              try {
                const data = JSON.parse(dataStr);
                if (currentEvent === "token") {
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantMessageId
                        ? { ...m, content: m.content + data.text }
                        : m
                    )
                  );
                } else if (currentEvent === "done") {
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantMessageId
                        ? { ...m, id: data.messageId }
                        : m
                    )
                  );
                  if (data.usage && onUsage) {
                    onUsage(data.usage);
                  }
                } else if (currentEvent === "error") {
                  setError(data.message);
                  if (data.code) setErrorCode(data.code);
                  setRetryable(!!data.retryable);
                  // Remove empty assistant placeholder on error
                  setMessages((prev) =>
                    prev.filter((m) => m.id !== assistantMessageId || m.content)
                  );
                }
              } catch {
                // skip malformed JSON
              }
              currentEvent = "";
            }
          }
        }
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          setError((err as Error).message);
          // Remove empty assistant placeholder on HTTP/network error
          setMessages((prev) =>
            prev.filter((m) => m.id !== assistantMessageId || m.content)
          );
        }
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [client, conversationId, ensureConversation, isStreaming]
  );

  const retry = useCallback(async () => {
    const lastContent = lastMessageRef.current;
    if (!lastContent || isStreaming) return;

    // Remove the failed assistant placeholder and the user message we'll re-send
    setMessages((prev) => {
      const lastUserIdx = prev.findLastIndex((m) => m.role === "user");
      if (lastUserIdx === -1) return prev;
      return prev.slice(0, lastUserIdx);
    });

    clearError();
    await sendMessage(lastContent);
  }, [isStreaming, sendMessage, clearError]);

  return { messages, isStreaming, error, errorCode, retryable, sendMessage, retry, clearError, resetMessages };
}
